import {useCallback, useEffect, useState} from 'react';
import type {ControlState, EligibilityReason} from './controlState';
import {deriveControlState} from './controlState';
import {
	getRunState,
	invoke,
	isAtRunCap,
	markRead,
	subscribeToAnyRunStateChange,
	subscribeToRunState,
} from './novaInvocationStore';

export interface NovaInvocationStateResult {
	state: ControlState;
	onAction: () => void;
	onOpenOutput: () => void;
}

const IDLE_RUN_STATE = {hasEverRun: false, isRunning: false, outputRead: false};

export function useNovaInvocationState(
	taskId: string,
	eligibility: EligibilityReason | null,
	// The caller (ConnectedNovaInvocationControl) must still call this hook unconditionally on
	// every render regardless of the feature flag — Rules of Hooks forbids skipping a hook call
	// itself. Passing `enabled` in lets the flag-off case skip the store subscriptions and their
	// per-render-and-per-event cost internally instead, since its result is discarded either way.
	enabled = true
): NovaInvocationStateResult {
	const [runState, setRunState] = useState(() => (enabled ? getRunState(taskId) : IDLE_RUN_STATE));
	const [atRunCap, setAtRunCap] = useState(() => enabled && isAtRunCap());

	useEffect(() => {
		if (!enabled) return undefined;
		return subscribeToRunState(taskId, () => setRunState(getRunState(taskId)));
	}, [taskId, enabled]);
	// The cap is a property of every tracked task, not just this one — another task starting or
	// finishing must be able to flip this idle control's disabled state too.
	useEffect(() => {
		if (!enabled) return undefined;
		return subscribeToAnyRunStateChange(() => setAtRunCap(isAtRunCap()));
	}, [enabled]);

	const state = deriveControlState({...runState, eligibility, atRunCap});

	// Bare Action always invokes the default route with no explicit agent. A user picking an
	// agent from the popover calls novaInvocationStore.invoke(taskId, agentId) directly
	// (see NovaAgentPickerForTask) — a separate call site, not through this hook.
	const onAction = useCallback(() => invoke(taskId), [taskId]);
	const onOpenOutput = useCallback(() => markRead(taskId), [taskId]);

	return {state, onAction, onOpenOutput};
}
